import React from 'react';
import cx from 'classnames';

import styles from './Touchable.module.scss';

export interface TouchableProps {
  ariaChecked?: boolean;
  ariaControls?: string;
  ariaExpanded?: boolean;
  ariaHasPopup?: boolean;
  ariaLabel?: string;
  ariaPressed?: boolean;
  block?: boolean;
  centered?: boolean;
  children?: React.ReactNode;
  className?: string;
  component?: React.ComponentType<any>;
  componentProps?: React.ComponentProps<any>;
  disabled?: boolean;
  hideFocus?: boolean;
  href?: string;
  id?: string;
  inline?: boolean;
  onClick?: (event: React.MouseEvent) => void;
  onFocus?: (event: React.MouseEvent) => void;
  onMouseEnter?: (event: React.MouseEvent) => void;
  onMouseLeave?: (event: React.MouseEvent) => void;
  onMouseOver?: (event: React.MouseEvent) => void;
  onBlur?: (event: React.KeyboardEvent) => void;
  ref?: any;
  rel?: string;
  role?: string;
  size?: 14 | 16;
  tabIndex?: number;
  target?: string;
  to?: string;
  type?: 'button' | 'submit' | 'reset';
  wide?: boolean;
}

export type Props = TouchableProps;

export const Touchable: React.ForwardRefExoticComponent<TouchableProps> = React.forwardRef(
  (
    {
      ariaChecked,
      ariaControls,
      ariaExpanded,
      ariaHasPopup,
      ariaLabel,
      ariaPressed,
      block,
      centered,
      children,
      className,
      component,
      componentProps,
      disabled,
      hideFocus,
      href,
      id,
      inline,
      onClick,
      onFocus,
      onMouseEnter,
      onMouseLeave,
      onMouseOver,
      onBlur,
      rel,
      role,
      size,
      tabIndex,
      target,
      to,
      type = 'button',
      wide,
      ...restProps
    },
    ref: React.RefObject<HTMLButtonElement>,
  ) => {
    const props = {
      className: cx(
        styles.resetButtonOrLink,
        inline && styles.inline,
        block && styles.block,
        wide && styles.wide,
        hideFocus && styles.hideFocus,
        centered && styles.centered,
        size === 14 && styles.size14,
        size === 16 && styles.size16,
        className,
      ),
      ...componentProps,
    };
    if (component) {
      const Component = component;
      return (
        <Component {...props} to={to} role={role} {...restProps}>
          {children}
        </Component>
      );
    }
    if (href) {
      // TODO(swb) consider a11y concerns of this scenario
      /*eslint jsx-a11y/anchor-has-content: 1 */
      return (
        <a
          {...props}
          aria-label={ariaLabel}
          aria-haspopup={ariaHasPopup}
          aria-expanded={ariaExpanded}
          href={href}
          id={id}
          role={role}
          target={target}
          rel={rel}
          onClick={onClick}
          onFocus={onFocus}
          onMouseEnter={onMouseEnter}
          onMouseLeave={onMouseLeave}
          onMouseOver={onMouseOver}
          onBlur={onBlur}
          ref={ref}
          {...restProps}
        >
          {children}
        </a>
      );
    }
    return (
      <button
        {...props}
        id={id}
        onClick={onClick}
        onFocus={onFocus}
        onMouseEnter={onMouseEnter}
        onMouseLeave={onMouseLeave}
        onMouseOver={onMouseOver}
        onBlur={onBlur}
        type={type}
        disabled={disabled}
        role={role}
        aria-checked={ariaChecked}
        aria-controls={ariaControls}
        aria-disabled={disabled}
        aria-expanded={ariaExpanded}
        aria-haspopup={ariaHasPopup}
        aria-label={ariaLabel}
        aria-pressed={ariaPressed}
        tabIndex={tabIndex}
        ref={ref}
        {...restProps}
      >
        {children}
      </button>
    );
  },
);

Touchable.displayName = 'Touchable';
export default Touchable;
export { styles };
